<!-- GlassNavbar.vue -->
<script setup lang="ts">
import { ref, computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faHome,
  faInfoCircle,
  faEnvelope,
  faSignInAlt,
  faUserPlus,
  faEllipsisH,
} from '@fortawesome/free-solid-svg-icons'
import { useAuthStore } from '@/stores/auth'

library.add(faHome, faInfoCircle, faEnvelope, faSignInAlt, faUserPlus, faEllipsisH)

// Props
defineProps<{
  isSidebarCollapsed?: boolean
}>()

const authStore = useAuthStore()
const isMenuOpen = ref(false)

const navItems = computed(() => {
  const items = [
    { name: 'Home', icon: 'home', path: '/', auth: false },
    { name: 'About', icon: 'info-circle', path: '/about', auth: false },
    { name: 'Contact', icon: 'envelope', path: '/contact', auth: false },
  ]

  if (!authStore.isAuthenticated) {
    items.push(
      { name: 'Login', icon: 'sign-in-alt', path: '/login', auth: false },
      { name: 'Register', icon: 'user-plus', path: '/register', auth: false },
    )
  }

  return items
})

const visibleItems = computed(() => {
  const maxVisible = 4
  return navItems.value.filter((item) => !item.auth).slice(0, maxVisible)
})

const hiddenItems = computed(() => {
  return navItems.value.filter((item) => !item.auth).slice(visibleItems.value.length)
})
</script>

<template>
  <!-- Header with navigation -->
  <header 
    class="sticky top-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-md shadow-sm h-16 transition-all duration-300 ease-in-out print:hidden"
    :class="[
      isSidebarCollapsed ? 'md:ml-50' : 'md:ml-0'
    ]"
  >
    <div class="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-full">
        <!-- Centered Logo -->
        <div
  class="absolute left-1/2 transform -translate-x-1/2"
  :class="[
    // md & lg behavior
    authStore.isAuthenticated
      ? (
          isSidebarCollapsed
            ? 'md:relative md:left-0 md:translate-x-0 md:ml-8'
            : 'md:absolute md:left-1/2 md:-translate-x-1/2'
        )
      : 'md:relative md:left-0 md:translate-x-0 md:ml-8'
  ]"
>
  <span class="text-xl md:text-2xl font-bold text-black-800">
    Rochex <span class="text-green-600">Investments</span>
  </span>
</div>


        <!-- Desktop Navigation (right side) -->
        <nav v-if="!authStore.isAuthenticated" class="hidden md:flex items-center space-x-6">
          <RouterLink
            v-for="item in navItems"
            :key="item.name"
            :to="item.path"
            class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-green-600 transition-colors duration-200 relative"
            :class="{ 'text-green-600': $route.path === item.path }"
          >
            {{ item.name }}
            <span
              v-if="$route.path === item.path"
              class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-0.5 bg-green-600 rounded-full"
            ></span>
          </RouterLink>
        </nav>
        <div v-else class="hidden md:block w-10"></div>
        <!-- Spacer for alignment -->
      </div>
    </div>
  </header>

  <!-- Mobile Bottom Navigation (for non-authenticated users only) -->
  <div
    v-if="!authStore.isAuthenticated"
    class="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-lg border-t border-gray-200 print:hidden"
  >
    <div class="flex justify-around items-center px-2 py-3">
      <!-- Visible Items -->
      <RouterLink
        v-for="item in visibleItems"
        :key="item.name"
        :to="item.path"
        class="flex flex-col items-center p-2 rounded-lg text-xs transition-colors duration-200"
        :class="{ 'text-green-600 bg-green-50': $route.path === item.path }"
      >
        <FontAwesomeIcon :icon="item.icon" class="h-5 w-5 mb-1" />
        <span>{{ item.name }}</span>
      </RouterLink>

      <!-- More Button -->
      <div v-if="hiddenItems.length > 0" class="relative">
        <button
          @click="isMenuOpen = !isMenuOpen"
          class="flex flex-col items-center p-2 rounded-lg text-xs transition-colors duration-200"
          :class="{ 'text-green-600 bg-green-50': isMenuOpen }"
        >
          <FontAwesomeIcon icon="ellipsis-h" class="h-5 w-5 mb-1" />
          <span>More</span>
        </button>

        <!-- Hidden Items Dropdown -->
        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <div
            v-show="isMenuOpen"
            class="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-lg shadow-xl py-1 z-50 border border-gray-200"
          >
            <RouterLink
              v-for="item in hiddenItems"
              :key="item.name"
              :to="item.path"
              class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors duration-200"
              @click="isMenuOpen = false"
            >
              <FontAwesomeIcon :icon="item.icon" class="mr-2 w-4 h-4" />
              {{ item.name }}
            </RouterLink>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
.router-link-active {
  color: #047857;
  font-weight: 500;
}
</style>
